import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import FilePreviewModal from './FilePreviewModal';
import SourcePreviewModal from './SourcePreviewModal';
import researchService from '../../services/researchService';
import xService from '../../services/xService';

const X_BACKFILL_STATUS = {
  queued: 'Queued',
  processing: 'Processing',
  processed: 'Processed',
  failed: 'Failed',
};

const normalizeBackfillStatus = (raw) => {
  const key = String(raw || '').trim().toLowerCase();
  if (key === 'queued') return X_BACKFILL_STATUS.queued;
  if (key === 'processing') return X_BACKFILL_STATUS.processing;
  if (key === 'processed') return X_BACKFILL_STATUS.processed;
  if (key === 'failed') return X_BACKFILL_STATUS.failed;
  return 'Idle';
};

const backfillBadgeClass = (status) => {
  const key = String(status || '').toLowerCase();
  if (key === 'processed') return 'bg-success';
  if (key === 'processing') return 'bg-primary';
  if (key === 'queued') return 'bg-warning text-dark';
  if (key === 'failed') return 'bg-danger';
  return 'bg-secondary';
};

const fmtDateTime = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString();
};

const isTranscriptFile = (file) => {
  const docType = String(file?.doc_type || '').toLowerCase();
  const key = String(file?.s3_key || '').toLowerCase();
  return docType.includes('transcript') || key.includes('/transcripts/');
};

const isXPostFile = (file) => {
  const docType = String(file?.doc_type || '').toLowerCase();
  const key = String(file?.s3_key || '').toLowerCase();
  return (
    docType === 'post-offical' ||
    docType === 'post-official' ||
    docType === 'post-trusted' ||
    key.includes('/x-posts/')
  );
};

const isMetadataSidecarFile = (file) => {
  const filename = String(file?.filename || '').toLowerCase();
  const key = String(file?.s3_key || '').toLowerCase();
  return filename.endsWith('.metadata.json') || key.endsWith('.metadata.json');
};

const byNewestUploaded = (a, b) => {
  const ta = new Date(a?.uploaded_at || a?.created_at || 0).getTime() || 0;
  const tb = new Date(b?.uploaded_at || b?.created_at || 0).getTime() || 0;
  return tb - ta;
};

const sourceStatusBadgeClass = (status) => {
  const map = {
    in_knowledgebase: 'bg-success',
    earnings_extracted: 'bg-primary',
    earnings_confirmed_no_changes: 'bg-primary',
    extraction_error: 'bg-danger',
    extraction_skipped: 'bg-warning text-dark',
    uploading: 'bg-secondary',
    stored_no_kb: 'bg-dark',
  };
  return map[status] || 'bg-secondary';
};

const scrollTableWrapperStyle = {
  maxHeight: '560px',
  overflowY: 'auto',
};

const stickyHeaderCellStyle = {
  position: 'sticky',
  top: 0,
  zIndex: 1,
};

const renderSourceFileTable = (
  items,
  { onRowClick, extraColumns = [], showDocType = true } = {},
) => (
  <div className="table-responsive mb-4" style={scrollTableWrapperStyle}>
    <table className="table table-sm table-hover align-middle">
      <thead className="table-light">
        <tr>
          <th style={stickyHeaderCellStyle}>File / Title</th>
          {showDocType && <th style={stickyHeaderCellStyle}>Doc Type</th>}
          {extraColumns.map((col) => (
            <th key={col.header} style={stickyHeaderCellStyle}>{col.header}</th>
          ))}
          <th style={stickyHeaderCellStyle}>Status</th>
          <th style={stickyHeaderCellStyle}>Doc Date</th>
          <th style={stickyHeaderCellStyle}>Uploaded</th>
        </tr>
      </thead>
      <tbody>
        {items.length === 0 ? (
          <tr>
            <td
              colSpan={4 + (showDocType ? 1 : 0) + extraColumns.length}
              className="text-muted small text-center py-3"
            >
              No files found for this source yet.
            </td>
          </tr>
        ) : (
          items.map((f) => (
            <tr
              key={f.file_id || f.s3_key || `${f.filename}-${f.last_modified || ''}`}
              style={onRowClick ? { cursor: 'pointer' } : undefined}
              role={onRowClick ? 'button' : undefined}
              tabIndex={onRowClick ? 0 : undefined}
              onClick={onRowClick ? () => onRowClick(f) : undefined}
              onKeyDown={onRowClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onRowClick(f); } } : undefined}
              title={onRowClick ? 'Click to preview' : undefined}
            >
              <td>
                <div className="fw-bold">{f.title || f.filename || 'Untitled'}</div>
                {f.title && <div className="small text-muted">{f.filename}</div>}
              </td>
              {showDocType && <td className="small text-muted">{f.doc_type || '—'}</td>}
              {extraColumns.map((col) => (
                <td key={col.header} className="small">
                  {col.render(f)}
                </td>
              ))}
              <td>
                <span className={`badge ${sourceStatusBadgeClass(f.status)}`} style={{ fontSize: '0.7rem' }}>
                  {(f.status || 'unknown').replace(/_/g, ' ')}
                </span>
              </td>
              <td className="small">{f.document_date || '—'}</td>
              <td className="small text-muted">
                {f.uploaded_at
                  ? new Date(f.uploaded_at).toLocaleString()
                  : (f.last_modified ? new Date(f.last_modified).toLocaleString() : '—')}
              </td>
            </tr>
          ))
        )}
      </tbody>
    </table>
  </div>
);

/**
 * Files tab for Company Research.
 *
 * Contains:
 * - Knowledge-base file list (search/filter/sort/preview/download/replace/delete)
 * - SEC filing documents list + preview
 */
export default function ResearchFilesTab({
  companyId,
  files,
  earnings,
  loadingData,
  onRefresh,
  onReplaceFile,
  onUpload,
}) {
  const [downloading, setDownloading] = useState(null);
  const [togglingKb, setTogglingKb] = useState(null);
  const [previewFile, setPreviewFile] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [cascadeEarnings, setCascadeEarnings] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const [secFilings, setSecFilings] = useState([]);
  const [loadingSecFilings, setLoadingSecFilings] = useState(false);
  const [previewFiling, setPreviewFiling] = useState(null);
  const [companyS3Files, setCompanyS3Files] = useState([]);
  const [loadingCompanyS3Files, setLoadingCompanyS3Files] = useState(false);
  const [companyConfig, setCompanyConfig] = useState(null);
  const [xResearchAccounts, setXResearchAccounts] = useState([]);
  const [loadingProgress, setLoadingProgress] = useState(false);
  const [backfillingAccountId, setBackfillingAccountId] = useState(null);
  const [accountBackfillError, setAccountBackfillError] = useState(null);

  // Source file preview (transcripts, x-posts)
  const [previewSourceFile, setPreviewSourceFile] = useState(null);
  const [previewSourceUrl, setPreviewSourceUrl] = useState(null);
  const [loadingSourceUrl, setLoadingSourceUrl] = useState(false);
  const sourceUrlCache = useRef({});

  // Section collapse state (all expanded by default)
  const [showTrackedFiles, setShowTrackedFiles] = useState(true);
  const [showTranscripts, setShowTranscripts] = useState(true);
  const [showXPosts, setShowXPosts] = useState(true);
  const [showSecFilings, setShowSecFilings] = useState(true);

  const fetchSecFilings = useCallback(async () => {
    if (!companyId) return;
    setLoadingSecFilings(true);
    try {
      const { filings } = await researchService.listSecFilings(companyId);
      setSecFilings(filings || []);
    } catch {
      setSecFilings([]);
    } finally {
      setLoadingSecFilings(false);
    }
  }, [companyId]);

  const fetchCompanyS3Files = useCallback(async () => {
    if (!companyId) return;
    setLoadingCompanyS3Files(true);
    try {
      const result = await researchService.listCompanyS3Files(companyId);
      setCompanyS3Files(result?.files || []);
    } catch (err) {
      console.warn('Could not load company S3 files', err);
      setCompanyS3Files([]);
    } finally {
      setLoadingCompanyS3Files(false);
    }
  }, [companyId]);

  const fetchBackfillProgress = useCallback(async () => {
    if (!companyId) return;
    setLoadingProgress(true);
    try {
      const [cfg, xCfg] = await Promise.all([
        researchService.getCompanyConfig(companyId),
        xService.getResearchAccounts(companyId),
      ]);
      setCompanyConfig(cfg || null);
      setXResearchAccounts(xCfg?.accounts || []);
    } catch (err) {
      console.warn('Could not load backfill progress snapshot', err);
      setCompanyConfig(null);
      setXResearchAccounts([]);
    } finally {
      setLoadingProgress(false);
    }
  }, [companyId]);

  const handleAccountBackfill = useCallback(async (account) => {
    const accountId = account?.id;
    if (!companyId || !accountId) return;
    setAccountBackfillError(null);
    setBackfillingAccountId(accountId);
    try {
      await xService.backfillResearchAccount(companyId, accountId, { years: 2 });
      await fetchBackfillProgress();
    } catch (err) {
      setAccountBackfillError(
        `@${account.handle}: ${err?.response?.data?.detail || err.message || 'Backfill failed'}`,
      );
    } finally {
      setBackfillingAccountId(null);
    }
  }, [companyId, fetchBackfillProgress]);

  useEffect(() => { fetchSecFilings(); }, [fetchSecFilings]);
  useEffect(() => { fetchCompanyS3Files(); }, [fetchCompanyS3Files]);
  useEffect(() => { fetchBackfillProgress(); }, [fetchBackfillProgress]);

  const handleSourceFileClick = useCallback(async (file) => {
    setPreviewSourceFile(file);

    const s3Uri = file?.s3_uri;
    if (!s3Uri) {
      console.warn('Missing s3_uri for source-file preview', file);
      setPreviewSourceUrl(null);
      return;
    }

    const cached = sourceUrlCache.current[s3Uri];
    setPreviewSourceUrl(cached !== undefined ? cached : null);
    if (cached !== undefined) return;

    setLoadingSourceUrl(true);
    try {
      const sidecarUri = `${s3Uri}.metadata.json`;
      const { url } = await researchService.presignSource(sidecarUri);
      const resp = await fetch(url);
      if (resp.ok) {
        const data = await resp.json();
        const srcUrl = data?.metadataAttributes?.source?.value?.stringValue || null;
        sourceUrlCache.current[s3Uri] = srcUrl || '';
        setPreviewSourceUrl(srcUrl);
      } else {
        sourceUrlCache.current[s3Uri] = '';
        setPreviewSourceUrl(null);
      }
    } catch (err) {
      console.warn('Could not load source URL metadata sidecar', err);
      sourceUrlCache.current[s3Uri] = '';
      setPreviewSourceUrl(null);
    } finally {
      setLoadingSourceUrl(false);
    }
  }, []);

  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterAuthor, setFilterAuthor] = useState('');
  const [sortCol, setSortCol] = useState('uploaded_at');
  const [sortDir, setSortDir] = useState('desc');

  const triggerDownload = (url, filename) => {
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const handleDownload = async (file) => {
    const key = file.file_id || file.s3_key;
    setDownloading(key);
    try {
      const { url, filename } = file.file_id
        ? await researchService.getDownloadUrl(file.file_id)
        : await researchService.presignSource(file.s3_uri);
      triggerDownload(url, filename || file.filename);
    } catch (err) {
      alert(`Download failed: ${err?.response?.data?.detail || err.message}`);
    } finally {
      setDownloading(null);
    }
  };

  const handleDeleteClick = (file) => {
    if (!file.file_id) return;
    const linkedPeriods = (earnings || [])
      .filter((e) => e.source_file_id === file.file_id)
      .map((e) => e.period_end_date);
    setCascadeEarnings(false);
    setDeleteTarget({ file, linkedPeriods });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await researchService.deleteFile(deleteTarget.file.file_id, cascadeEarnings);
      setDeleteTarget(null);
      if (onRefresh) onRefresh();
    } catch (err) {
      alert(`Delete failed: ${err?.response?.data?.detail || err.message}`);
    } finally {
      setDeleting(false);
    }
  };

  const isFileInKnowledgeBase = (file) => {
    if (typeof file.include_in_knowledge_base === 'boolean') {
      return file.include_in_knowledge_base;
    }
    const normalizedStatus = String(file.status || '').toLowerCase().replace(/\s+/g, '_');
    if (normalizedStatus === 'stored_no_kb') {
      return false;
    }
    return !(file.s3_key || '').includes('/no_kb/');
  };

  const handleToggleKb = async (file) => {
    if (!file.file_id) return;
    const currentlyInKb = isFileInKnowledgeBase(file);
    setTogglingKb(file.file_id);
    try {
      await researchService.setFileKbMembership(file.file_id, !currentlyInKb);
      if (onRefresh) onRefresh();
    } catch (err) {
      alert(`KB membership update failed: ${err?.response?.data?.detail || err.message}`);
    } finally {
      setTogglingKb(null);
    }
  };

  const allCompanyFiles = useMemo(
    () => (companyS3Files.length > 0 ? companyS3Files : (files || []))
      .filter((file) => !isMetadataSidecarFile(file)),
    [companyS3Files, files],
  );
  const researchUploadFiles = useMemo(
    () => allCompanyFiles.filter((f) => f.source === 'research_uploads' || !f.source),
    [allCompanyFiles],
  );

  const uniqueTypes = useMemo(() =>
    [...new Set(researchUploadFiles.map((f) => f.doc_type).filter(Boolean))].sort(), [researchUploadFiles]);
  const uniqueStatuses = useMemo(() =>
    [...new Set(researchUploadFiles.map((f) => f.status).filter(Boolean))].sort(), [researchUploadFiles]);
  const uniqueAuthors = useMemo(() =>
    [...new Set(researchUploadFiles.map((f) => f.author).filter(Boolean))].sort(), [researchUploadFiles]);

  const handleSort = (col) => {
    if (sortCol === col) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortCol(col); setSortDir('asc'); }
  };

  const sortIcon = (col) => {
    if (sortCol !== col) return <i className="bi bi-arrow-down-up text-muted ms-1" style={{ fontSize: '0.7rem' }} />;
    return sortDir === 'asc'
      ? <i className="bi bi-arrow-up ms-1" style={{ fontSize: '0.7rem' }} />
      : <i className="bi bi-arrow-down ms-1" style={{ fontSize: '0.7rem' }} />;
  };

  const visibleFiles = useMemo(() => {
    let list = researchUploadFiles;
    const q = search.trim().toLowerCase();
    if (q) list = list.filter((f) =>
      (f.filename || '').toLowerCase().includes(q) ||
      (f.title || '').toLowerCase().includes(q)
    );
    if (filterType) list = list.filter((f) => f.doc_type === filterType);
    if (filterStatus) list = list.filter((f) => f.status === filterStatus);
    if (filterAuthor) list = list.filter((f) => f.author === filterAuthor);

    const dir = sortDir === 'asc' ? 1 : -1;
    list = [...list].sort((a, b) => {
      const va = (a[sortCol] || '').toString();
      const vb = (b[sortCol] || '').toString();
      return va < vb ? -dir : va > vb ? dir : 0;
    });
    return list;
  }, [researchUploadFiles, search, filterType, filterStatus, filterAuthor, sortCol, sortDir]);

  const hasFilters = search || filterType || filterStatus || filterAuthor;
  const transcriptFiles = useMemo(
    () => allCompanyFiles.filter(isTranscriptFile).sort(byNewestUploaded),
    [allCompanyFiles],
  );
  const xPostFiles = useMemo(
    () => allCompanyFiles.filter(isXPostFile).sort(byNewestUploaded),
    [allCompanyFiles],
  );
  const latestTranscriptUploadAt = useMemo(() => {
    if (!transcriptFiles.length) return null;
    return transcriptFiles.reduce((latest, file) => {
      const ts = new Date(file?.uploaded_at || file?.created_at || file?.last_modified || 0).getTime();
      if (!Number.isFinite(ts) || ts <= 0) return latest;
      return !latest || ts > latest ? ts : latest;
    }, null);
  }, [transcriptFiles]);
  const hasYoutubeTracking = Boolean(
    companyConfig?.youtube_subscription_id || companyConfig?.youtube_channel_id,
  );

  const xBackfillCounts = useMemo(
    () => xResearchAccounts.reduce(
      (acc, account) => {
        const s = normalizeBackfillStatus(account?.backfill_status).toLowerCase();
        if (s === 'queued') acc.queued += 1;
        else if (s === 'processing') acc.processing += 1;
        else if (s === 'processed') acc.processed += 1;
        else if (s === 'failed') acc.failed += 1;
        else acc.idle += 1;
        return acc;
      },
      { queued: 0, processing: 0, processed: 0, failed: 0, idle: 0 },
    ),
    [xResearchAccounts],
  );

  const badge = (docType) => {
    const cls = docType === 'earnings' ? 'bg-info text-dark' : 'bg-secondary';
    return <span className={`badge ${cls} text-uppercase`}>{docType}</span>;
  };

  const statusBadge = (status) => {
    const map = {
      in_knowledgebase: 'bg-success',
      earnings_extracted: 'bg-primary',
      earnings_confirmed_no_changes: 'bg-primary',
      extraction_error: 'bg-danger',
      extraction_skipped: 'bg-warning text-dark',
      uploading: 'bg-secondary',
      stored_no_kb: 'bg-dark',
    };
    const cls = map[status] || 'bg-secondary';
    return <span className={`badge ${cls}`} style={{ fontSize: '0.7rem' }}>{status?.replace(/_/g, ' ')}</span>;
  };

  const thStyle = { cursor: 'pointer', whiteSpace: 'nowrap', userSelect: 'none' };

  return (
    <div className="p-3">
      <div className="d-flex justify-content-between align-items-center mb-2 mt-3">
        <h6 className="fw-bold mb-0">Files</h6>
      </div>

      {loadingData ? (
        <div className="text-muted small">Loading…</div>
      ) : (
        <>

          <div className="border rounded bg-light mb-3 mt-2">
            <div className="d-flex justify-content-between align-items-center p-3">
              <h6 className="fw-semibold mb-0">
                <i className="bi bi-activity me-1" />Backfill Progress
              </h6>
              <button
                className="btn btn-sm btn-outline-secondary"
                onClick={fetchBackfillProgress}
                disabled={loadingProgress}
              >
                <i className="bi bi-arrow-clockwise me-1" />Refresh status
              </button>
            </div>
            <div className="px-3 pb-3">

              {companyConfig?.backfill_job ? (
                <div className="small mb-1">
                  <strong>Tracked Metrics Backfill:</strong>{' '}
                  <span className={`badge ${backfillBadgeClass(companyConfig.backfill_job.status || 'idle')} me-2`}>
                    {companyConfig.backfill_job.status || 'Idle'}
                  </span>
                  Updated {(companyConfig.backfill_job.periods_updated || []).length} period(s)
                  {(companyConfig.backfill_job.errors || []).length > 0 && (
                    <span className="text-danger ms-2">
                      {(companyConfig.backfill_job.errors || []).length} error(s)
                    </span>
                  )}
                </div>
              ) : (
                <div className="small text-muted mb-1">Tracked Metrics Backfill: no job recorded yet.</div>
              )}

              {companyConfig?.api_backfill_job ? (
                <div className="small mb-1">
                  <strong>SEC API Backfill:</strong>{' '}
                  <span className={`badge ${backfillBadgeClass(companyConfig.api_backfill_job.status || 'idle')} me-2`}>
                    {companyConfig.api_backfill_job.status || 'Idle'}
                  </span>
                  Wrote {(companyConfig.api_backfill_job.periods_written || []).length} period(s), skipped {(companyConfig.api_backfill_job.periods_skipped || []).length}
                  {(companyConfig.api_backfill_job.errors || []).length > 0 && (
                    <span className="text-danger ms-2">
                      {(companyConfig.api_backfill_job.errors || []).length} error(s)
                    </span>
                  )}
                </div>
              ) : (
                <div className="small text-muted mb-1">SEC API Backfill: no job recorded yet.</div>
              )}

              <div className="small mb-1">
                <strong>YouTube Transcript Backfill:</strong>{' '}
                {hasYoutubeTracking ? (
                  <>
                    {transcriptFiles.length} transcript file(s) archived
                    {latestTranscriptUploadAt && (
                      <span className="text-muted ms-2">(latest {fmtDateTime(latestTranscriptUploadAt)})</span>
                    )}
                  </>
                ) : (
                  <span className="text-muted">no YouTube subscription configured yet.</span>
                )}
              </div>

              <div className="small mb-0">
                <strong>X Backfill:</strong>{' '}
                queued {xBackfillCounts.queued}, processing {xBackfillCounts.processing}, processed {xBackfillCounts.processed}, failed {xBackfillCounts.failed}
                {xResearchAccounts.length > 0 && (
                  <span className="text-muted ms-2">(across {xResearchAccounts.length} monitored account(s))</span>
                )}
              </div>
              {accountBackfillError && (
                <div className="small text-danger mt-2 mb-0">{accountBackfillError}</div>
              )}
              {xResearchAccounts.length > 0 && (
                <div className="table-responsive mt-2" style={scrollTableWrapperStyle}>
                  <table className="table table-sm table-bordered mb-0" style={{ fontSize: '0.78rem' }}>
                    <thead className="table-light sticky-top">
                      <tr>
                        <th>Handle</th>
                        <th>Type</th>
                        <th>Status</th>
                        <th>Last Checked</th>
                        <th>Updated</th>
                        <th>Error</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {xResearchAccounts.map((a) => {
                        const normalized = normalizeBackfillStatus(a.backfill_status);
                        const isBackfilling = backfillingAccountId === a.id;
                        const isBusy = backfillingAccountId != null;
                        return (
                          <tr key={a.id || `${a.company_id}-${a.handle}-${a.account_type}`}>
                            <td>@{a.handle}</td>
                            <td className="text-capitalize text-muted">{a.account_type || '—'}</td>
                            <td><span className={`badge ${backfillBadgeClass(normalized)}`}>{normalized}</span></td>
                            <td className="text-muted">{fmtDateTime(a.last_checked_at)}</td>
                            <td className="text-muted">{fmtDateTime(a.updated_at)}</td>
                            <td className="text-danger">{a.backfill_error || '—'}</td>
                            <td>
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-primary py-0 px-2"
                                disabled={isBusy || !a.id}
                                onClick={() => handleAccountBackfill(a)}
                                title={`Queue a backfill for @${a.handle}`}
                              >
                                {isBackfilling ? (
                                  <>
                                    <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true" />
                                    Queuing...
                                  </>
                                ) : 'Backfill'}
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>

          <div
            className="d-flex justify-content-between align-items-center mb-0 mt-3"
            style={{ cursor: 'pointer' }}
            onClick={() => setShowTrackedFiles((v) => !v)}
          >
            <h6 className="fw-bold mb-0">
              <i className={`bi bi-chevron-${showTrackedFiles ? 'down' : 'right'} me-2 text-muted`} style={{ fontSize: '0.75rem' }} />
              Research Files
            </h6>
            <div className="d-flex align-items-center gap-2" onClick={(e) => e.stopPropagation()}>
              <button className="btn btn-sm btn-primary" onClick={onUpload} disabled={!companyId}>
                <i className="bi bi-cloud-upload me-1" />Upload File
              </button>
              <button className="btn btn-sm btn-outline-secondary" onClick={fetchCompanyS3Files} disabled={loadingCompanyS3Files}>
                <i className="bi bi-arrow-clockwise me-1" />Refresh
              </button>
            </div>
          </div>

          {showTrackedFiles && (
            <>


              <div className="small text-muted mb-2 mt-2">
                Showing every research-upload file for this company (database-tracked and any not-yet-tracked S3 objects) in one place.
              </div>

              <div className="d-flex flex-wrap gap-2 mb-2 align-items-center">
                <div className="input-group input-group-sm" style={{ maxWidth: 260 }}>
                  <span className="input-group-text"><i className="bi bi-search" /></span>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search filename…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                  {search && (
                    <button className="btn btn-outline-secondary" onClick={() => setSearch('')}>
                      <i className="bi bi-x" />
                    </button>
                  )}
                </div>

                <select className="form-select form-select-sm" style={{ maxWidth: 130 }}
                  value={filterType} onChange={(e) => setFilterType(e.target.value)}>
                  <option value="">All types</option>
                  {uniqueTypes.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>

                <select className="form-select form-select-sm" style={{ maxWidth: 190 }}
                  value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
                  <option value="">All statuses</option>
                  {uniqueStatuses.map((s) => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
                </select>

                {uniqueAuthors.length > 0 && (
                  <select className="form-select form-select-sm" style={{ maxWidth: 160 }}
                    value={filterAuthor} onChange={(e) => setFilterAuthor(e.target.value)}>
                    <option value="">All authors</option>
                    {uniqueAuthors.map((a) => <option key={a} value={a}>{a}</option>)}
                  </select>
                )}

                {hasFilters && (
                  <button className="btn btn-sm btn-link text-muted p-0"
                    onClick={() => { setSearch(''); setFilterType(''); setFilterStatus(''); setFilterAuthor(''); }}>
                    Clear filters
                  </button>
                )}

                <span className="text-muted small ms-auto">
                  {visibleFiles.length} of {researchUploadFiles.length}
                </span>
              </div>

              <div className="table-responsive mb-4" style={scrollTableWrapperStyle}>
                <table className="table table-sm table-hover align-middle">
                  <thead className="table-light">
                    <tr>
                      <th style={{ ...thStyle, ...stickyHeaderCellStyle }} onClick={() => handleSort('doc_type')}>
                        Type{sortIcon('doc_type')}
                      </th>
                      <th style={{ ...thStyle, ...stickyHeaderCellStyle }} onClick={() => handleSort('filename')}>
                        File / Title{sortIcon('filename')}
                      </th>
                      <th style={{ ...thStyle, ...stickyHeaderCellStyle }} onClick={() => handleSort('author')}>
                        Author{sortIcon('author')}
                      </th>
                      <th style={{ ...thStyle, ...stickyHeaderCellStyle }} onClick={() => handleSort('document_date')}>
                        Doc date{sortIcon('document_date')}
                      </th>
                      <th style={{ ...thStyle, ...stickyHeaderCellStyle }} onClick={() => handleSort('status')}>
                        Status{sortIcon('status')}
                      </th>
                      <th style={{ ...thStyle, ...stickyHeaderCellStyle }} onClick={() => handleSort('uploaded_at')}>
                        Uploaded{sortIcon('uploaded_at')}
                      </th>
                      <th style={stickyHeaderCellStyle}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibleFiles.length === 0 ? (
                      <tr><td colSpan={7} className="text-muted small text-center py-3">No files match the current filters.</td></tr>
                    ) : visibleFiles.map((f) => {
                      const rowKey = f.file_id || f.s3_key;
                      const isTracked = Boolean(f.file_id);
                      return (
                        <tr
                          key={rowKey}
                          style={{ cursor: 'pointer' }}
                          role="button"
                          tabIndex={0}
                          onClick={() => setPreviewFile(f)}
                          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setPreviewFile(f); } }}
                        >
                          <td>{badge(f.doc_type)}</td>
                          <td>
                            <div className="fw-bold">{f.title || f.filename}</div>
                            {f.title && <div className="small text-muted">{f.filename}</div>}
                            {!isTracked && (
                              <div className="small text-muted fst-italic">Not yet tracked in database</div>
                            )}
                          </td>
                          <td className="small">{f.author || '—'}</td>
                          <td className="small">{f.document_date || '—'}</td>
                          <td>{statusBadge(f.status)}</td>
                          <td className="small text-muted">{f.uploaded_at?.split('T')[0]}</td>
                          <td onClick={(e) => e.stopPropagation()}>
                            <div className="d-flex gap-1">
                              <button className="btn btn-sm btn-outline-primary" title="Download" aria-label="Download"
                                disabled={downloading === rowKey} onClick={() => handleDownload(f)}>
                                {downloading === rowKey
                                  ? <span className="spinner-border spinner-border-sm" />
                                  : <i className="bi bi-download" />}
                              </button>
                              <button className="btn btn-sm btn-outline-secondary" title={isTracked ? 'Replace with new version' : 'Not tracked in database yet'}
                                disabled={!isTracked}
                                onClick={() => onReplaceFile && onReplaceFile(f)}>
                                <i className="bi bi-arrow-repeat" />
                              </button>
                              <button
                                className="btn btn-sm btn-outline-warning"
                                title={
                                  !isTracked
                                    ? 'Not tracked in database yet'
                                    : isFileInKnowledgeBase(f)
                                      ? 'Move out of knowledge base'
                                      : 'Move to knowledge base'
                                }
                                disabled={!isTracked || togglingKb === f.file_id}
                                onClick={() => handleToggleKb(f)}
                              >
                                {togglingKb === f.file_id
                                  ? <span className="spinner-border spinner-border-sm" />
                                  : (!isFileInKnowledgeBase(f)
                                    ? <i className="bi bi-cloud-arrow-up" />
                                    : <i className="bi bi-cloud-slash" />)}
                              </button>
                              <button className="btn btn-sm btn-outline-danger" title={isTracked ? 'Delete from knowledge base' : 'Not tracked in database yet'}
                                disabled={!isTracked}
                                onClick={() => handleDeleteClick(f)}>
                                <i className="bi bi-trash" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}

          <div
            className="d-flex justify-content-between align-items-center mb-0 mt-3"
            style={{ cursor: 'pointer' }}
            onClick={() => setShowTranscripts((v) => !v)}
          >
            <h6 className="fw-bold mb-0">
              <i className={`bi bi-chevron-${showTranscripts ? 'down' : 'right'} me-2 text-muted`} style={{ fontSize: '0.75rem' }} />
              <i className="bi bi-file-earmark-play me-1 text-danger" />
              Transcript Files
            </h6>
            <button className="btn btn-sm btn-outline-secondary" onClick={(e) => { e.stopPropagation(); fetchCompanyS3Files(); }} disabled={loadingCompanyS3Files}>
              <i className="bi bi-arrow-clockwise me-1" />Refresh
            </button>
          </div>
          {showTranscripts && (
            <>
              <p className="text-muted small mb-2 mt-2">
                Files archived from YouTube transcript ingestion for this company.
              </p>
              {renderSourceFileTable(transcriptFiles, {
                onRowClick: handleSourceFileClick,
                showDocType: false,
                extraColumns: [
                  {
                    header: 'Video Title',
                    render: (f) => f.video_title
                      ? <span className="small">{f.video_title}</span>
                      : <span className="text-muted small">{f.video_id || f.filename?.replace(/\.txt$/i, '') || '\u2014'}</span>,
                  },
                  {
                    header: 'Watch',
                    render: (f) => f.video_url
                      ? (
                        <a
                          href={f.video_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-sm btn-outline-danger py-0 px-2"
                          onClick={(e) => e.stopPropagation()}
                          title={`Watch on YouTube: ${f.video_title || f.video_id || ''}`}
                        >
                          <i className="bi bi-youtube" />
                        </a>
                      )
                      : '\u2014',
                  },
                ],
              })}
            </>
          )}

          <div
            className="d-flex justify-content-between align-items-center mb-0 mt-3"
            style={{ cursor: 'pointer' }}
            onClick={() => setShowXPosts((v) => !v)}
          >
            <h6 className="fw-bold mb-0">
              <i className={`bi bi-chevron-${showXPosts ? 'down' : 'right'} me-2 text-muted`} style={{ fontSize: '0.75rem' }} />
              <i className="bi bi-twitter-x me-1" />
              X Posts Files
            </h6>
            <button className="btn btn-sm btn-outline-secondary" onClick={(e) => { e.stopPropagation(); fetchCompanyS3Files(); }} disabled={loadingCompanyS3Files}>
              <i className="bi bi-arrow-clockwise me-1" />Refresh
            </button>
          </div>
          {showXPosts && (
            <>
              <p className="text-muted small mb-2 mt-2">
                Files archived from Official and Trusted X accounts.
              </p>
              {renderSourceFileTable(xPostFiles, { onRowClick: handleSourceFileClick })}
            </>
          )}
        </>
      )}

      <div
        className="d-flex justify-content-between align-items-center mb-0 mt-3"
        style={{ cursor: 'pointer' }}
        onClick={() => setShowSecFilings((v) => !v)}
      >
        <h6 className="fw-bold mb-0">
          <i className={`bi bi-chevron-${showSecFilings ? 'down' : 'right'} me-2 text-muted`} style={{ fontSize: '0.75rem' }} />
          <i className="bi bi-file-earmark-text me-1 text-secondary" />
          SEC Filing Documents
          <span className="text-muted fw-normal small ms-2">(10-Q / 10-K — not in knowledge base)</span>
        </h6>
        <button className="btn btn-sm btn-outline-secondary" onClick={(e) => { e.stopPropagation(); fetchSecFilings(); }} disabled={loadingSecFilings}>
          <i className="bi bi-arrow-clockwise me-1" />Refresh
        </button>
      </div>

      {showSecFilings && (loadingSecFilings ? (
        <div className="text-muted small mt-2">Loading…</div>
      ) : secFilings.length === 0 ? (
        <div className="text-muted small mb-4 mt-2">No SEC filings downloaded yet. Run a backfill to fetch them.</div>
      ) : (
        <div className="table-responsive mb-4 mt-2" style={scrollTableWrapperStyle}>
          <table className="table table-sm table-hover align-middle">
            <thead className="table-light">
              <tr>
                <th style={stickyHeaderCellStyle}>Form</th>
                <th style={stickyHeaderCellStyle}>Period</th>
                <th style={stickyHeaderCellStyle}>Filename</th>
                <th style={stickyHeaderCellStyle}>Size</th>
                <th style={stickyHeaderCellStyle}>Saved</th>
              </tr>
            </thead>
            <tbody>
              {secFilings.map((f) => (
                <tr
                  key={f.s3_uri}
                  style={{ cursor: 'pointer' }}
                  role="button"
                  tabIndex={0}
                  onClick={() => setPreviewFiling(f)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setPreviewFiling(f); } }}
                  title="Click to preview"
                >
                  <td>
                    <div className="d-flex align-items-center gap-2">
                      <span className={`badge ${f.form_type === '10-K' ? 'bg-primary' : 'bg-info text-dark'
                        } text-uppercase`}>{f.form_type || '—'}</span>
                      <button
                        type="button"
                        className="btn btn-sm btn-link p-0 text-secondary"
                        title="Open in new tab"
                        aria-label="Open filing in new tab"
                        onClick={async (e) => {
                          e.stopPropagation();
                          try {
                            const { url } = await researchService.presignSource(f.s3_uri);
                            window.open(url, '_blank', 'noopener,noreferrer');
                          } catch { /* ignore */ }
                        }}
                      >
                        <i className="bi bi-box-arrow-up-right" />
                      </button>
                    </div>
                  </td>
                  <td className="small fw-bold">{f.period_end_date || '—'}</td>
                  <td className="small text-muted">{f.filename}</td>
                  <td className="small text-muted">
                    {f.size_bytes != null ? `${(f.size_bytes / 1024).toFixed(0)} KB` : '—'}
                  </td>
                  <td className="small text-muted">{f.last_modified?.split('T')[0] || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}

      {previewFile && (
        <FilePreviewModal file={previewFile} onClose={() => setPreviewFile(null)} />
      )}

      {previewFiling && (
        <SourcePreviewModal
          s3Uri={previewFiling.s3_uri}
          filename={previewFiling.filename}
          onClose={() => setPreviewFiling(null)}
        />
      )}

      {previewSourceFile && (
        <SourcePreviewModal
          s3Uri={previewSourceFile.s3_uri}
          filename={previewSourceFile.filename || previewSourceFile.title}
          sourceUrl={loadingSourceUrl ? undefined : (previewSourceUrl || undefined)}
          onClose={() => { setPreviewSourceFile(null); setPreviewSourceUrl(null); }}
        />
      )}

      {deleteTarget && (
        <>
          <div className="modal-backdrop fade show" />
          <div className="modal fade show d-block" tabIndex="-1" role="dialog" aria-modal="true">
            <div className="modal-dialog modal-dialog-centered" role="document">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">
                    <i className="bi bi-trash me-2 text-danger" />Delete file
                  </h5>
                  <button type="button" className="btn-close"
                    onClick={() => setDeleteTarget(null)} disabled={deleting} />
                </div>
                <div className="modal-body">
                  <p>
                    Are you sure you want to remove <strong>{deleteTarget.file.filename}</strong> from
                    the knowledge base? This cannot be undone.
                  </p>
                  {deleteTarget.linkedPeriods.length > 0 && (
                    <div className="alert alert-warning py-2">
                      <p className="mb-1 fw-bold">
                        <i className="bi bi-exclamation-triangle me-1" />
                        This file is the source for {deleteTarget.linkedPeriods.length} structured earnings period{deleteTarget.linkedPeriods.length > 1 ? 's' : ''}:
                      </p>
                      <ul className="mb-2 small">
                        {deleteTarget.linkedPeriods.map((p) => <li key={p}>{p}</li>)}
                      </ul>
                      <div className="form-check">
                        <input className="form-check-input" type="checkbox" id="cascade-earnings-check"
                          checked={cascadeEarnings} onChange={(e) => setCascadeEarnings(e.target.checked)} />
                        <label className="form-check-label" htmlFor="cascade-earnings-check">
                          Also delete the structured earnings data for these periods
                        </label>
                      </div>
                    </div>
                  )}
                </div>
                <div className="modal-footer">
                  <button className="btn btn-secondary" onClick={() => setDeleteTarget(null)} disabled={deleting}>
                    Cancel
                  </button>
                  <button className="btn btn-danger" onClick={handleDeleteConfirm} disabled={deleting}>
                    {deleting
                      ? <><span className="spinner-border spinner-border-sm me-1" />Deleting…</>
                      : 'Delete'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
